import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { permisosApi } from '../services/api';
import { formatFecha } from '../utils/dateFormat';
import { Shield, RefreshCw, AlertTriangle, Check, List, LayoutGrid } from 'lucide-react';

// ── Etiquetas ────────────────────────────────────────────────────────────────
const ACCIONES_LABEL = {
  leer: 'Leer', crear: 'Crear', editar: 'Editar', eliminar: 'Eliminar', exportar: 'Exportar',
};
const ROL_LABEL = {
  admin: 'Admin',
  dir_comercial: 'Dir. Comercial',
  comercial: 'Comercial',
  agente_libre: 'Agente libre',
  admin_comercial: 'Admin Comercial',
};
const CONDICION_LABEL = {
  global: 'Global', solo_zona: 'Solo zona', solo_propias: 'Solo propias',
};

// Nombres legibles de recurso en la UI
const RECURSO_LABEL = {
  empresas: 'Empresas / Clientes',
  contactos: 'Contactos',
  contratos: 'Contratos',
  visitas: 'Visitas',
  tareas: 'Tareas',
  proyectos: 'Proyectos',
  grupos: 'Grupos empresariales',
  documentos_comerciales: 'Ventas y Métricas',
  pedidos: 'Pedidos',
  albaranes: 'Albaranes',
  tarifas: 'Tarifas',
  articulos: 'Artículos ERP',
  productos: 'Productos',
  homologaciones: 'Homologaciones',
  importacion: 'Importación',
  exportacion: 'Exportación',
  ia: 'IA / Asistente',
  perplexity: 'Prospección IA',
  inteligencia_municipal: 'Mapa municipios',
  noticias: 'Avisos / Noticias',
  correos: 'Correos',
  usuarios: 'Usuarios',
  zonas: 'Zonas',
  campos_config: 'Campos personalizados',
  audit_log: 'Auditoría',
  erp_sync: 'Sincronización ERP',
  scheduler: 'Scheduler',
  permisos: 'Permisos del sistema',
};

// Agrupación visual de recursos
const GRUPOS_RECURSOS = [
  { label: 'CRM', recursos: ['empresas', 'contactos', 'contratos', 'visitas', 'tareas', 'proyectos', 'grupos'] },
  { label: 'Ventas', recursos: ['documentos_comerciales', 'pedidos', 'albaranes', 'tarifas', 'articulos', 'productos', 'homologaciones', 'importacion', 'exportacion'] },
  { label: 'IA y Comunicaciones', recursos: ['ia', 'perplexity', 'inteligencia_municipal', 'noticias', 'correos'] },
  { label: 'Sistema', recursos: ['usuarios', 'zonas', 'campos_config', 'audit_log', 'erp_sync', 'scheduler', 'permisos'] },
];

// Recursos donde el selector "Alcance" no aplica (siempre global)
const RECURSOS_SOLO_GLOBAL = new Set([
  'grupos', 'documentos_comerciales', 'albaranes', 'tarifas', 'articulos',
  'productos', 'homologaciones', 'importacion', 'exportacion',
  'ia', 'perplexity', 'inteligencia_municipal', 'noticias', 'correos',
  'usuarios', 'zonas', 'campos_config', 'audit_log', 'erp_sync', 'scheduler', 'permisos',
]);

// Recursos con require_permission real en el backend
const RECURSOS_CONECTADOS = new Set([
  'empresas', 'contactos', 'contratos', 'visitas', 'tareas',
  'proyectos', 'productos', 'homologaciones', 'pedidos', 'exportacion', 'correos',
]);

// Autores que corresponden a seeds automáticos (no se muestran en la UI como cambios manuales)
const AUTORES_SEED = new Set(['seed', 'seed_nuevos_recursos', 'migration_iter55']);

// ── Componente compacto: letras coloreadas por acción ────────────────────────
const AccionesBadge = ({ acciones }) => {
  const letras = [
    { key: 'leer', l: 'L' },
    { key: 'crear', l: 'C' },
    { key: 'editar', l: 'E' },
    { key: 'eliminar', l: 'D' },
    { key: 'exportar', l: 'X' },
  ];
  return (
    <span className="flex gap-0.5 font-mono text-[11px]">
      {letras.map(({ key, l }) => (
        <span key={key} className={acciones?.[key] ? 'font-bold text-emerald-600' : 'text-ink-200'}>
          {l}
        </span>
      ))}
    </span>
  );
};

// Devuelve texto de modificación si fue cambiado manualmente
const updatedInfo = (entry) => {
  if (!entry?.updated_at || !entry?.updated_by) return null;
  if (AUTORES_SEED.has(entry.updated_by)) return null;
  const fecha = formatFecha(entry.updated_at);
  return fecha ? `${entry.updated_by} · ${fecha}` : entry.updated_by;
};

// ── Componente principal ─────────────────────────────────────────────────────
export default function AdminPermisos() {
  const [modoVista, setModoVista] = useState('por_rol');
  const [catalogo, setCatalogo] = useState(null);
  const [matriz, setMatriz] = useState(null);
  const [rolActivo, setRolActivo] = useState('comercial');
  const [savingKey, setSavingKey] = useState(null);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);

  const recargar = async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([permisosApi.catalogo(), permisosApi.matriz()]);
      setCatalogo(c);
      setMatriz(m.matriz);
    } catch (e) {
      setMsg({ tipo: 'error', texto: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { recargar(); }, []);

  const toggleAccion = async (rol, recurso, accion) => {
    const current = matriz[rol][recurso];
    const nuevasAcciones = { ...current.acciones, [accion]: !current.acciones[accion] };
    const key = `${rol}-${recurso}-${accion}`;
    setSavingKey(key);
    try {
      const res = await permisosApi.actualizar(rol, recurso, {
        acciones: nuevasAcciones,
        condicion: current.condicion,
      });
      setMatriz((prev) => ({
        ...prev,
        [rol]: { ...prev[rol], [recurso]: { ...prev[rol][recurso], acciones: res.patch.acciones, condicion: res.patch.condicion } },
      }));
      setMsg({ tipo: 'ok', texto: `${RECURSO_LABEL[recurso] || recurso} · ${ACCIONES_LABEL[accion]} actualizado` });
    } catch (e) {
      setMsg({ tipo: 'error', texto: e.message });
    } finally {
      setSavingKey(null);
      setTimeout(() => setMsg(null), 2500);
    }
  };

  const cambiarCondicion = async (rol, recurso, nuevaCond) => {
    const current = matriz[rol][recurso];
    setSavingKey(`${rol}-${recurso}-cond`);
    try {
      const res = await permisosApi.actualizar(rol, recurso, { acciones: current.acciones, condicion: nuevaCond });
      setMatriz((prev) => ({
        ...prev,
        [rol]: { ...prev[rol], [recurso]: { ...prev[rol][recurso], acciones: res.patch.acciones, condicion: res.patch.condicion } },
      }));
    } catch (e) {
      setMsg({ tipo: 'error', texto: e.message });
    } finally {
      setSavingKey(null);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('¿Restablecer toda la matriz a los defaults? Se perderán tus cambios.')) return;
    setLoading(true);
    try {
      await permisosApi.resetDefaults();
      await recargar();
      setMsg({ tipo: 'ok', texto: 'Matriz restaurada a defaults' });
    } catch (e) {
      setMsg({ tipo: 'error', texto: e.message });
    } finally {
      setLoading(false);
    }
  };

  // Agrupación de recursos disponibles según catálogo del backend
  const gruposConRecursos = useMemo(() => {
    if (!catalogo) return [];
    const disponibles = new Set(catalogo.recursos);
    const enGrupos = new Set(GRUPOS_RECURSOS.flatMap((g) => g.recursos));
    const resultado = GRUPOS_RECURSOS
      .map((g) => ({ ...g, recursos: g.recursos.filter((r) => disponibles.has(r)) }))
      .filter((g) => g.recursos.length > 0);
    const otros = catalogo.recursos.filter((r) => !enGrupos.has(r));
    if (otros.length) resultado.push({ label: 'Otros', recursos: otros });
    return resultado;
  }, [catalogo]);

  if (loading || !catalogo || !matriz) {
    return <div className="p-8 text-ink-500">Cargando matriz de permisos…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-permisos">
      {/* ── Cabecera ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-bold text-ink-900 flex items-center gap-3">
            <Shield className="w-7 h-7 text-brand-500" />
            Permisos configurables
          </h1>
          <p className="text-ink-500 mt-1">
            Matriz rol × recurso × acción. Los cambios son efectivos <strong>al instante</strong> sin reiniciar el servidor.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Selector de vista */}
          <div className="flex rounded-lg border border-ink-200 overflow-hidden text-sm">
            <button
              onClick={() => setModoVista('por_rol')}
              className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors ${modoVista === 'por_rol' ? 'bg-brand-500 text-white' : 'bg-white text-ink-600 hover:bg-ink-50'}`}
            >
              <List className="w-4 h-4" /> Por rol
            </button>
            <button
              onClick={() => setModoVista('por_recurso')}
              className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors border-l border-ink-200 ${modoVista === 'por_recurso' ? 'bg-brand-500 text-white' : 'bg-white text-ink-600 hover:bg-ink-50'}`}
            >
              <LayoutGrid className="w-4 h-4" /> Por recurso
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={recargar}>
            <RefreshCw className="w-4 h-4 mr-1" /> Recargar
          </Button>
          <Button variant="outline" size="sm" onClick={resetDefaults}>
            <AlertTriangle className="w-4 h-4 mr-1" /> Restaurar defaults
          </Button>
        </div>
      </div>

      {msg && (
        <div className={`px-4 py-2 rounded-md text-sm ${msg.tipo === 'ok' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}>
          {msg.texto}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          VISTA: POR ROL
      ══════════════════════════════════════════════════════════ */}
      {modoVista === 'por_rol' && (
        <>
          {/* Tabs de rol */}
          <div className="flex flex-wrap gap-2">
            {catalogo.roles.map((r) => (
              <button
                key={r}
                onClick={() => setRolActivo(r)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  rolActivo === r
                    ? 'bg-brand-500 text-white shadow'
                    : 'bg-white text-ink-700 border border-ink-200 hover:border-brand-300'
                }`}
              >
                {ROL_LABEL[r] || r}
              </button>
            ))}
          </div>

          {/* Tabla agrupada */}
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-left sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 font-semibold text-ink-700 min-w-[200px]">Recurso</th>
                  {catalogo.acciones.map((a) => (
                    <th key={a} className="px-3 py-3 font-semibold text-ink-700 text-center whitespace-nowrap">
                      {ACCIONES_LABEL[a] || a}
                    </th>
                  ))}
                  <th className="px-4 py-3 font-semibold text-ink-700 whitespace-nowrap">Alcance</th>
                </tr>
              </thead>
              <tbody>
                {gruposConRecursos.map((grupo) => (
                  <React.Fragment key={grupo.label}>
                    {/* Cabecera de grupo */}
                    <tr>
                      <td
                        colSpan={catalogo.acciones.length + 2}
                        className="px-4 py-2 bg-ink-100 text-xs font-bold text-ink-500 uppercase tracking-wider"
                      >
                        {grupo.label}
                      </td>
                    </tr>
                    {grupo.recursos.map((recurso, idx) => {
                      const entry = matriz[rolActivo]?.[recurso] || { acciones: {}, condicion: 'global' };
                      const isInvariante = rolActivo === 'admin' && recurso === 'permisos';
                      const conectado = RECURSOS_CONECTADOS.has(recurso);
                      const soloGlobal = RECURSOS_SOLO_GLOBAL.has(recurso);
                      const modInfo = updatedInfo(entry);
                      return (
                        <tr key={recurso} className={idx % 2 ? 'bg-ink-50/40' : 'bg-white'}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-start gap-2">
                              <div>
                                <span className="font-medium text-ink-900">
                                  {RECURSO_LABEL[recurso] || recurso}
                                </span>
                                {modInfo && (
                                  <div className="text-[10px] text-ink-400 mt-0.5">{modInfo}</div>
                                )}
                              </div>
                              {isInvariante && (
                                <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 shrink-0">
                                  invariante
                                </Badge>
                              )}
                              {conectado ? (
                                <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50 shrink-0" title="require_permission activo en el backend">
                                  activo
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] border-ink-300 text-ink-400 bg-ink-50 shrink-0" title="Endpoints usan require_role — configuración documental pendiente de conectar">
                                  pendiente
                                </Badge>
                              )}
                            </div>
                          </td>
                          {catalogo.acciones.map((a) => {
                            const activa = !!entry.acciones[a];
                            const key = `${rolActivo}-${recurso}-${a}`;
                            const saving = savingKey === key;
                            return (
                              <td key={a} className="px-3 py-2.5 text-center">
                                <button
                                  disabled={saving || isInvariante}
                                  onClick={() => toggleAccion(rolActivo, recurso, a)}
                                  className={`w-8 h-8 rounded-md border transition-all ${
                                    activa
                                      ? 'bg-emerald-500 border-emerald-600 text-white'
                                      : 'bg-white border-ink-200 text-ink-400 hover:border-ink-400'
                                  } ${saving ? 'opacity-50' : ''} ${isInvariante ? 'cursor-not-allowed opacity-60' : ''}`}
                                >
                                  {saving ? '…' : activa ? <Check className="w-4 h-4 mx-auto" /> : ''}
                                </button>
                              </td>
                            );
                          })}
                          <td className="px-4 py-2.5">
                            {soloGlobal || rolActivo === 'admin' ? (
                              <span className="text-xs text-ink-400 italic">—</span>
                            ) : (
                              <select
                                value={entry.condicion || 'global'}
                                onChange={(e) => cambiarCondicion(rolActivo, recurso, e.target.value)}
                                className="text-xs border border-ink-200 rounded px-2 py-1 bg-white"
                              >
                                {catalogo.condiciones.map((c) => (
                                  <option key={c} value={c}>{CONDICION_LABEL[c] || c}</option>
                                ))}
                              </select>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════
          VISTA: POR RECURSO (resumen de todos los roles a la vez)
      ══════════════════════════════════════════════════════════ */}
      {modoVista === 'por_recurso' && (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-left">
              <tr>
                <th className="px-4 py-3 font-semibold text-ink-700 min-w-[200px]">Recurso</th>
                {catalogo.roles.map((r) => (
                  <th key={r} className="px-4 py-3 font-semibold text-ink-700 text-center whitespace-nowrap">
                    {ROL_LABEL[r] || r}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {gruposConRecursos.map((grupo) => (
                <React.Fragment key={grupo.label}>
                  <tr>
                    <td
                      colSpan={catalogo.roles.length + 1}
                      className="px-4 py-2 bg-ink-100 text-xs font-bold text-ink-500 uppercase tracking-wider"
                    >
                      {grupo.label}
                    </td>
                  </tr>
                  {grupo.recursos.map((recurso, idx) => (
                    <tr key={recurso} className={idx % 2 ? 'bg-ink-50/40' : 'bg-white'}>
                      <td className="px-4 py-2.5">
                        <div>
                          <span className="font-medium text-ink-900">{RECURSO_LABEL[recurso] || recurso}</span>
                          {!RECURSOS_CONECTADOS.has(recurso) && (
                            <span className="ml-2 text-[10px] text-ink-400 italic">pendiente</span>
                          )}
                        </div>
                      </td>
                      {catalogo.roles.map((rol) => {
                        const entry = matriz[rol]?.[recurso];
                        const acciones = entry?.acciones || {};
                        const tieneAcceso = Object.values(acciones).some(Boolean);
                        return (
                          <td
                            key={rol}
                            className="px-4 py-2.5 text-center"
                            title={`${ROL_LABEL[rol]}: ${catalogo.acciones.filter((a) => acciones[a]).map((a) => ACCIONES_LABEL[a]).join(', ') || 'Sin acceso'}`}
                          >
                            {tieneAcceso ? (
                              <AccionesBadge acciones={acciones} />
                            ) : (
                              <span className="text-ink-200 text-xs">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 border-t border-ink-100 bg-ink-50 text-xs text-ink-500 flex items-center gap-4">
            <span className="font-mono font-bold text-emerald-600">L</span> Leer ·
            <span className="font-mono font-bold text-emerald-600">C</span> Crear ·
            <span className="font-mono font-bold text-emerald-600">E</span> Editar ·
            <span className="font-mono font-bold text-emerald-600">D</span> Eliminar ·
            <span className="font-mono font-bold text-emerald-600">X</span> Exportar ·
            <span className="text-ink-300">letra gris = sin acceso</span>
          </div>
        </Card>
      )}

      {/* ── Nota informativa ──────────────────────────────────────── */}
      <Card className="p-4 bg-brand-50/50 border-brand-200">
        <div className="flex items-start gap-3 text-sm">
          <AlertTriangle className="w-5 h-5 text-brand-600 shrink-0 mt-0.5" />
          <div className="text-ink-700 space-y-1.5">
            <div>
              <strong>Regla invariante:</strong> el rol <code className="px-1 bg-white rounded">admin</code> siempre conserva acceso a <code className="px-1 bg-white rounded">permisos.leer/editar</code>.
            </div>
            <div>
              <strong>Recursos <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50">activo</Badge>:</strong> los endpoints usan <code className="px-1 bg-white rounded">require_permission</code> — los toggles tienen efecto real inmediato.
            </div>
            <div>
              <strong>Recursos <Badge variant="outline" className="text-[10px] border-ink-300 text-ink-400 bg-ink-50">pendiente</Badge>:</strong> la configuración se guarda pero los endpoints aún usan <code className="px-1 bg-white rounded">require_role</code> hardcoded. Quedan preparados para conectar.
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
